import type {
  AnonymizerIntegrationReadiness,
  BankAccountConfig,
  BankCardConfig,
  BirthDateConfig,
  DictionariesConfig,
  FioConfig,
  InnConfig,
  PassportConfig,
  PhoneConfig,
  SnilsConfig,
  UpdateFioConfigRequest,
  UploadDictionariesResponse,
} from './types';

import { apiClient } from '@/shared/api';

// Задержка-заглушка для ещё не реализованной на бэкенде загрузки словарей
const STUB_DELAY = 300;
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// --- GET функции (чтение конфигурации класса из сервиса анонимизатора) ---

const fetchConfig = async <T>(className: string): Promise<T> => {
  const response = await apiClient.get<T>(`/config/${className}`);
  return response.data;
};

export const fetchPhoneConfig = () => fetchConfig<PhoneConfig>('phone');
export const fetchFioConfig = () => fetchConfig<FioConfig>('fio');
export const fetchBirthDateConfig = () => fetchConfig<BirthDateConfig>('birth-date');
export const fetchPassportConfig = () => fetchConfig<PassportConfig>('passport');
export const fetchSnilsConfig = () => fetchConfig<SnilsConfig>('snils');
export const fetchInnConfig = () => fetchConfig<InnConfig>('inn');
export const fetchBankCardConfig = () => fetchConfig<BankCardConfig>('bank-card');
export const fetchBankAccountConfig = () => fetchConfig<BankAccountConfig>('bank-account');

// Словари входят в конфигурацию ФИО — читаем общий конфиг класса и возвращаем его секцию словарей
export const fetchFioDictionaries = async (): Promise<DictionariesConfig> => {
  const config = await fetchFioConfig();
  return config.dictionaries;
};

/**
 * Готовность интеграции с анонимизатором. Открыта по праву на настройку анонимизации —
 * в отличие от сведений об интеграции, которые требуют права на раздел интеграций.
 */
export const fetchAnonymizerIntegrationReadiness = async (): Promise<AnonymizerIntegrationReadiness> => {
  const response = await apiClient.get<AnonymizerIntegrationReadiness>('/config/integration-readiness');
  return response.data;
};

// --- PUT функции (частичное обновление конфигурации в сервисе; слияние выполняет сервис) ---

const updateConfig = async <T>(className: string, body: unknown): Promise<T> => {
  const response = await apiClient.put<T>(`/config/${className}`, body);
  return response.data;
};

export const updatePhoneConfig = (body: PhoneConfig) => updateConfig<PhoneConfig>('phone', body);
export const updateFioConfig = (body: UpdateFioConfigRequest) => updateConfig<FioConfig>('fio', body);
export const updateBirthDateConfig = (body: BirthDateConfig) => updateConfig<BirthDateConfig>('birth-date', body);
export const updatePassportConfig = (body: PassportConfig) => updateConfig<PassportConfig>('passport', body);
export const updateSnilsConfig = (body: SnilsConfig) => updateConfig<SnilsConfig>('snils', body);
export const updateInnConfig = (body: InnConfig) => updateConfig<InnConfig>('inn', body);
export const updateBankCardConfig = (body: BankCardConfig) => updateConfig<BankCardConfig>('bank-card', body);
export const updateBankAccountConfig = (body: BankAccountConfig) =>
  updateConfig<BankAccountConfig>('bank-account', body);

// Заглушка: реальный HTTP-вызов загрузки словарей пока не реализован
export const uploadFioDictionaries = async (_formData: FormData): Promise<UploadDictionariesResponse> => {
  await delay(STUB_DELAY);
  return { namesCount: 1500, surnamesCount: 3200, patronymicsCount: 800 };
};

export const uploadBenchmarkTest = async (formData: FormData): Promise<void> => {
  await apiClient.post('/config/benchmark/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
};

// --- Роутеры по className ---

type ConfigFetcher = () => Promise<unknown>;
type ConfigUpdater = (body: unknown) => Promise<unknown>;

const configFetchers: Record<string, ConfigFetcher> = {
  phone: fetchPhoneConfig,
  fio: fetchFioConfig,
  'fio/dictionaries': fetchFioDictionaries,
  'birth-date': fetchBirthDateConfig,
  passport: fetchPassportConfig,
  snils: fetchSnilsConfig,
  inn: fetchInnConfig,
  'bank-card': fetchBankCardConfig,
  'bank-account': fetchBankAccountConfig,
};

const configUpdaters: Record<string, ConfigUpdater> = {
  phone: updatePhoneConfig as ConfigUpdater,
  fio: updateFioConfig as ConfigUpdater,
  'birth-date': updateBirthDateConfig as ConfigUpdater,
  passport: updatePassportConfig as ConfigUpdater,
  snils: updateSnilsConfig as ConfigUpdater,
  inn: updateInnConfig as ConfigUpdater,
  'bank-card': updateBankCardConfig as ConfigUpdater,
  'bank-account': updateBankAccountConfig as ConfigUpdater,
};

export const fetchConfigByClassName = (className: string) => {
  const fetcher = configFetchers[className];
  if (!fetcher) throw new Error(`Unknown class: ${className}`);
  return fetcher();
};

export const updateConfigByClassName = (className: string, body: unknown) => {
  const updater = configUpdaters[className];
  if (!updater) throw new Error(`Unknown class: ${className}`);
  return updater(body);
};
